import { useMutation, useQueryClient } from "@tanstack/react-query";

import type { EventPrefix } from "@/features/analytics/models";
import { EVENT_PREFIX } from "@/features/analytics/models";
import { trackPaymentCompleted } from "@/features/analytics/trackers";
import { trackPaymentFailed } from "@/features/analytics/trackers/payment-failed";
import {
  billingQueriesStore,
  createSubscriptionsAndPatient,
} from "@/features/billing/api";
import type { OnboardingUser } from "@/features/onboarding/models";
import { useOnboardingStore } from "@/features/onboarding/stores";
import type { CheckoutDiscount } from "@/features/payments/api";
import type { CategoryWithSelectedProduct } from "@/features/products/models";
import { errorToast } from "@/shared/ui";

interface UseCreateSubscriptionsAndPatientParams {
  user?: OnboardingUser;
  checkoutDiscount?: CheckoutDiscount;
  selectedCategories: CategoryWithSelectedProduct[];
  eventPrefix?: EventPrefix;
}

export const useCreateSubscriptionsAndPatient = ({
  user,
  selectedCategories,
  eventPrefix = EVENT_PREFIX.NONE,
}: UseCreateSubscriptionsAndPatientParams) => {
  const queryClient = useQueryClient();

  const setUserToken = useOnboardingStore((state) => state.setUserToken);
  const metadata = useOnboardingStore((state) => state.urlParams);

  return useMutation({
    mutationFn: createSubscriptionsAndPatient,
    onSuccess: ({ token, paymentIntentIds, invoiceIds }) => {
      setUserToken(token);

      void queryClient.invalidateQueries({
        queryKey: billingQueriesStore.subscriptions.queryKey,
      });

      if (user) {
        trackPaymentCompleted({
          categories: selectedCategories,
          succeededPaymentIds: paymentIntentIds,
          succeededInvoiceIds: invoiceIds,
          user,
          metadata,
          eventPrefix,
        });
      }
    },
    onError: (error) => {
      errorToast(error);

      if (user) {
        trackPaymentFailed({
          user,
          categories: selectedCategories,
          eventPrefix,
        });
      }
    },
  });
};
